import { useEffect } from 'react';
import { useQuery } from 'react-query';
import api from '../../../lib/api';
import { useSessionStore } from '../../../store/sessionStore';

/**
 * Session resume + hydration side effects for the test session view:
 *  - on mount (when navigated from a dashboard "Resume" action) initialise the
 *    session and skip pre-flight, and leave the session on unmount;
 *  - restore `sessionElementType` from the server when it isn't in the store;
 *  - restore previously-recorded results from the server into the store.
 *
 * Extracted verbatim from TestSessionView. Purely side-effecting — returns nothing.
 */
export function useSessionResume({
  resumeSiteId,
  resumeSessionId,
  _hasHydrated,
  sessionId,
  jobStarted,
  sessionElementType,
  isSyncing,
  initSession,
  setJobStarted,
  setSessionElementType,
  registerResult,
}) {
  // Dashboard "Resume": Init session on mount and skip pre-flight
  useEffect(() => {
    if (!_hasHydrated) return;
    if (resumeSiteId != null && String(resumeSiteId).trim() !== '') {
      initSession(String(resumeSiteId).trim(), resumeSessionId);
      setJobStarted(true);
    }

    return () => {
      useSessionStore.getState().leaveSession();
    };
  }, [_hasHydrated, resumeSiteId, resumeSessionId, initSession]);

  // --- RESUME: Fetch session metadata to restore sessionElementType when not in store ---
  useQuery(
    ['session', sessionId],
    async () => {
      if (!sessionId) return null;
      const { data } = await api.get(`/sessions/${sessionId}`);
      return data;
    },
    {
      enabled: !!sessionId && jobStarted && sessionElementType == null,
      onSuccess: (session) => {
        // Idempotency guard: only write when the value actually changes, so a
        // refetch can't trigger a redundant store write / re-render.
        if (
          session?.element_type &&
          session.element_type !== useSessionStore.getState().sessionElementType
        ) {
          setSessionElementType(session.element_type);
        }
      },
      staleTime: Infinity,
    }
  );

  // --- RESUME DATA: Fetch existing results from server if resuming ---
  // Skip request if sessionId no longer matches store (stale closure after reset/leave).
  // Prevents 404 polling loop when user finished session or navigated away.
  useQuery(
    ['sessionResults', sessionId],
    async () => {
      if (!sessionId) return;
      const currentSessionId = useSessionStore.getState().sessionId;
      if (currentSessionId !== sessionId) return; // Stale — session switched or cleared
      const { data } = await api.get(`/sessions/${sessionId}/results`);
      return data;
    },
    {
      enabled: !!sessionId && !isSyncing && _hasHydrated,
      onSuccess: (data) => {
        if (!Array.isArray(data)) return;
        data.forEach((row) => {
          // Idempotency guard: a window-focus / reconnect refetch must not
          // double-register or clobber results we already hold in the store.
          const existing = useSessionStore.getState().results[row.asset_id];
          if (existing) {
            // Unsynced local edit — keep the technician's pending result
            // (overwriting would mark it synced and drop it from the sync queue).
            if (existing.synced === false) return;
            // Already registered with this same server test id — nothing to do.
            if (existing.testId != null && String(existing.testId) === String(row.id)) return;
          }
          registerResult(row.asset_id, {
            result: row.result,
            testId: row.id,
            tripTimeMs: row.metrics?.trip_time_ms ?? null,
          });
        });
      },
      staleTime: Infinity,
      retry: false, // Avoid 404 retry loop when session doesn't exist on server
    }
  );
}
